import { useState, useCallback } from 'react';

const usePagination = (initialPage = 1, initialPageSize = 10) => {
  const [pagination, setPagination] = useState({
    currentPage: initialPage,
    pageSize: initialPageSize,
    totalItems: 0,
    totalPages: 1,
  });

  const updatePagination = useCallback(({ currentPage, totalItems, pageSize }) => {
    const totalPages = Math.ceil(totalItems / pageSize) || 1;
    setPagination(prev => ({
      ...prev,
      currentPage: Math.min(currentPage, totalPages) || 1,
      totalItems,
      pageSize: pageSize || prev.pageSize,
      totalPages,
    }));
  }, []);

  const goToPage = useCallback((page) => {
    setPagination(prev => ({
      ...prev,
      currentPage: Math.max(1, Math.min(page, prev.totalPages)),
    }));
  }, []);

  const nextPage = useCallback(() => {
    setPagination(prev => ({
      ...prev,
      currentPage: Math.min(prev.currentPage + 1, prev.totalPages),
    }));
  }, []);

  const prevPage = useCallback(() => {
    setPagination(prev => ({
      ...prev,
      currentPage: Math.max(1, prev.currentPage - 1),
    }));
  }, []);

  const setPageSize = useCallback((size) => {
    const newPageSize = Math.max(1, parseInt(size, 10) || 10);
    setPagination(prev => {
      const newTotalPages = Math.ceil(prev.totalItems / newPageSize) || 1;
      return {
        ...prev,
        pageSize: newPageSize,
        currentPage: Math.min(prev.currentPage, newTotalPages),
        totalPages: newTotalPages,
      };
    });
  }, []);

  return {
    ...pagination,
    updatePagination,
    goToPage,
    nextPage,
    prevPage,
    setPageSize,
    hasNextPage: pagination.currentPage < pagination.totalPages,
    hasPrevPage: pagination.currentPage > 1,
    offset: (pagination.currentPage - 1) * pagination.pageSize,
  };
};

export default usePagination;
